import React from 'react';
import { makeStyles, tokens, Dropdown, Option, Field } from '@fluentui/react-components';

// Backward-compatible types: legacy field `url_suffix`, new standardized field `apiPath`.
type RawAccessItem = { url_suffix?: string; apiPath?: string; name: string };
type AccessItem = { apiPath: string; name: string };

const API_VERSION_OPTIONS = [
  '2024-10-21',
  '2024-12-01-preview',
  '2025-04-01-preview'
];

const useStyles = makeStyles({
  container: {
    marginBottom: tokens.spacingVerticalM,
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
});

interface ApiSelectorsProps {
  selectedApiVersion: string;
  selectedApiPath: string;
  items: AccessItem[] | null;
  isProcessing: boolean;
  isLoading: boolean;
  hasError: boolean;
  onApiVersionChange: (version: string) => void;
  onApiPathChange: (path: string) => void;
}

export default function ApiSelectors({
  selectedApiVersion,
  selectedApiPath,
  items,
  isProcessing,
  isLoading,
  hasError,
  onApiVersionChange,
  onApiPathChange
}: ApiSelectorsProps) {
  const styles = useStyles();
  const isDisabled = isProcessing || isLoading;

  return (
    <>
      {/* API Version Selection */}
      <Field label="API Version" required style={{ minWidth: 200 }}>
        <Dropdown
          value={selectedApiVersion}
          selectedOptions={[selectedApiVersion]}
          onOptionSelect={(_, data) => onApiVersionChange(data.optionValue ?? '')}
          disabled={isProcessing}
        >
          {API_VERSION_OPTIONS.map((version) => (
            <Option key={version} value={version} text={version}>{version}</Option>
          ))}
        </Dropdown>
      </Field>

      {/* API Path Selection */}
      <Field label="API Path" required style={{ minWidth: 300 }}>
        <Dropdown
          value={selectedApiPath ? `${items?.find(i => i.apiPath === selectedApiPath)?.name ?? ''} (${selectedApiPath})` : ''}
          selectedOptions={selectedApiPath ? [selectedApiPath] : []}
          onOptionSelect={(_, data) => onApiPathChange(data.optionValue ?? '')}
          disabled={isDisabled}
          placeholder={isLoading ? 'Loading API paths...' : hasError ? 'Error loading API paths' : !items || items.length === 0 ? 'No API paths available' : 'Select an API Path...'}
        >
          {(items ?? []).map((item, index) => (
            <Option key={`${item.apiPath}-${index}`} value={item.apiPath} text={`${item.name} (${item.apiPath})`}>
              {item.name} ({item.apiPath})
            </Option>
          ))}
        </Dropdown>
      </Field>
    </>
  );
}

// Export types for use in other components
export type { AccessItem, RawAccessItem };
export { API_VERSION_OPTIONS };